<template>
  <div class="mb-4 flex justify-end py-2">
    <div class="flex justify-between space-x-2">
      <DateFilter
        :model-value="timePeriod"
        @update:model-value="setTimePeriod"
      />
    </div>
  </div>
  <HeadlessDisclosure
    #default="{ open: isComparisonDrawerOpen }"
  >
    <BaseCard class="mb-4">
      <template v-if="!isComparisonDrawerOpen">
        <template v-if="localQueries.length === 1">
          <div
            class="my-1 flex w-full flex-wrap items-center justify-end space-x-2 border-l-2 border-transparent pl-2 pt-2"
          >
            <div
              class="grid w-full flex-1 grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4"
            >
              <ReportFilterSelect
                v-model="localQueries[0].cashierId"
                label="Cashier"
                :options="cashierOptions"
                class="w-full"
              />
              <ReportDeviceFilter
                v-model="localQueries[0].deviceId"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[0].categoryId"
                label="Category"
                :options="categoryOptions"
                class="w-full"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[0].productTagId"
                label="Product Tag"
                :options="productTagOptions"
                class="w-full"
              >
                <template #option="{ option }">
                  <ProductTagChip
                    :product-tag-id="option.value as string"
                  />
                </template>
              </ReportFilterSelect>

              <div
                class="col-end:2 flex flex-shrink-0 items-center justify-end space-x-2 sm:col-end-3 md:col-end-5"
              >
                <ButtonExport
                  :is-loading="isExporting"
                  @export="
                    (format) => onExport(queries[0], format)
                  "
                />

                <ButtonWithIcon
                  :disabled="
                    isLocalQueryEmpty(localQueries[0])
                  "
                  @click="removeLocalQuery(0)"
                >
                  <IconHeroiconsXMark20Solid />
                </ButtonWithIcon>
              </div>
            </div>
          </div>
        </template>
      </template>

      <HeadlessDisclosurePanel>
        <template
          v-for="(localQuery, index) in localQueries"
          :key="index"
        >
          <div
            class="my-1 flex w-full flex-wrap items-center justify-end space-x-2 border-l-2 py-2 pl-2"
            :style="{
              borderColor: getColor(
                localQueries[index].colorVariableName
              ),
            }"
          >
            <div
              class="grid w-full min-w-48 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4"
            >
              <ReportFilterSelect
                v-model="localQueries[index].cashierId"
                label="Cashier"
                :options="cashierOptions"
                class="w-full"
              />
              <ReportDeviceFilter
                v-model="localQueries[index].deviceId"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[index].categoryId"
                label="Category"
                :options="categoryOptions"
                class="w-full"
              />
              <ReportFilterSelect
                v-if="tenant.isProductsEnabled"
                v-model="localQueries[index].productTagId"
                label="Product Tag"
                :options="productTagOptions"
                class="w-full"
              >
                <template #option="{ option }">
                  <ProductTagChip
                    :product-tag-id="option.value as string"
                  />
                </template>
              </ReportFilterSelect>
              <div
                class="col-end:2 flex flex-shrink-0 items-center justify-end space-x-2 sm:col-end-3 md:col-end-5"
              >
                <ButtonExport
                  :disabled="isExporting"
                  @export="
                    (format) =>
                      onExport(queries[index], format)
                  "
                />

                <ButtonWithIcon
                  :disabled="
                    localQueries.length === 1 &&
                    isLocalQueryEmpty(localQueries[0])
                  "
                  @click="removeLocalQuery(index)"
                >
                  <IconHeroiconsSolidTrash
                    v-if="localQueries.length > 1"
                  />
                  <IconHeroiconsXMark20Solid v-else />
                </ButtonWithIcon>
              </div>
            </div>
          </div>
        </template>
        <template v-if="localQueries.length < 10">
          <div
            class="flex w-full items-center justify-start space-x-2"
          >
            <button
              :class="[
                'flex items-center justify-center space-x-2 rounded-lg px-3 py-1',
                'hover:bg-background-fg/3',
              ]"
              type="button"
              @click="addLocalQuery"
            >
              <span> Add Dataset </span>
              <IconHeroiconsSolidPlus />
            </button>
          </div>
        </template>
      </HeadlessDisclosurePanel>
      <div class="flex w-full justify-center">
        <HeadlessDisclosureButton
          :class="[
            'flex w-full items-center justify-center rounded-lg py-2',
            'hover:bg-background-fg/3',
          ]"
        >
          <template v-if="!isComparisonDrawerOpen">
            <span> Show Datasets </span>
            <IconHeroiconsSolidChevronDown
              class="h-6 w-6"
            />
          </template>
          <template v-else>
            <span> Hide </span>
            <IconHeroiconsSolidChevronUp class="h-6 w-6" />
          </template>
        </HeadlessDisclosureButton>
      </div>
    </BaseCard>
  </HeadlessDisclosure>

  <BaseCard class="relative">
    <div class="flex w-full flex-col">
      <div class="flex justify-end py-2">
        <ReportViewToggle v-model="graphView" />
      </div>
      <div
        class="md:h-128 h-64 w-full overflow-hidden sm:h-96"
      >
        <template v-if="datasets[0].data.length > 0">
          <template v-if="graphView === 'trend'">
            <ChartBarDate
              id="sales-trend-graph"
              :labels="labels"
              :datasets="datasets"
              :granularity="granularity"
              is-cumulative
              :currency="tenant.currency"
              @hover-datapoint="handleHoverDatapoint"
            />
          </template>
          <template v-else-if="graphView === 'cumulative'">
            <ChartLineDate
              id="sales-cumulative-graph"
              :labels="labels"
              :datasets="datasets"
              :granularity="granularity"
              is-cumulative
              :currency="tenant.currency"
              @hover-datapoint="handleHoverDatapoint"
            />
          </template>
        </template>
        <template v-else>
          <BaseInnerLoading />
        </template>
      </div>

      <div
        class="bg-background-fg/5 flex-shrink-0 rounded-lg p-4"
      >
        <template v-if="hoveredData">
          <div class="mb-2">
            <div class="text-background-fg/70 text-sm">
              {{ hoveredData.formattedLabel }}
            </div>
          </div>
          <div
            class="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            <template
              v-for="(
                dataset, index
              ) in hoveredData.datasets"
              :key="dataset.color"
            >
              <div class="flex items-center space-x-4">
                <ColorItem :value="dataset.color" rounded />
                <div
                  class="flex flex-1 flex-wrap items-center justify-start gap-2"
                >
                  <template
                    v-for="filter in getFiltersForIndex(
                      index
                    )"
                    :key="filter"
                  >
                    <span
                      class="bg-primary text-primary-fg rounded-full px-3 py-1 text-sm"
                    >
                      {{ filter }}
                    </span>
                  </template>
                </div>
                <div class="w-36">
                  <p
                    class="text-background-fg/70 text-center text-sm leading-tight"
                  >
                    Sales Total
                  </p>
                  <p class="text-center font-semibold">
                    {{
                      formatToMerchantCurrencyString(
                        dataset.value
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import type { SalesReportQuery } from "@/services/reports/retrieveSalesReport"
import type { HoveredDataPoint } from "@/components/ChartLineDate.vue"
import type { GraphView } from "@/components/ReportViewToggle.vue"
import type { Color } from "@/composables/options/useColorOptions"
import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"
import type { LocalQuery } from "@/composables/useReportQuery"

const { tenant } = useTenant()
const { formatToMerchantCurrencyString } = useCurrency()
const {
  options: cashierOptions,
  getLabelForValue: getCashierLabel,
} = useCashierOptions()
const {
  asOptions: categoryOptions,
  getLabelForValue: getCategoryLabel,
} = useCategories()

const {
  asOptions: productTagOptions,
  getById: getProductTagById,
} = useProductTags()
const emptyLocalQuery = {
  cashierId: undefined,
  categoryId: undefined,
}

const isLocalQueryEmpty = (
  query: LocalQuery<SalesReportQuery>
) =>
  query.cashierId === emptyLocalQuery.cashierId &&
  query.categoryId === emptyLocalQuery.categoryId &&
  !query.deviceId &&
  !query.productTagId

const getColor = (colorVariableName: Color | undefined) =>
  colorVariableName
    ? `rgb(var(${colorVariableName}))`
    : undefined

const {
  queries,
  localQueries,
  addLocalQuery,
  removeLocalQuery,
  setTimePeriod,
  timePeriod,
} = useReportQuery<SalesReportQuery>(emptyLocalQuery)

const queryResults = useSalesReports(queries)

const rawDatasets = computed(() =>
  queryResults.map(({ data }) => data?.dataset)
)

const granularity = computed(
  () => queryResults?.[0]?.data?.granularity
)

const labels = computed(
  () =>
    rawDatasets.value?.[0]?.map(({ start, end }) => ({
      start,
      end,
    })) || []
)

const datasets = computed(() =>
  rawDatasets.value.map((rawDataset, index) => {
    const colorVariableName =
      localQueries[index].colorVariableName
    const colorVariable = colorVariableName
      ? useCssVar(colorVariableName).value
      : undefined
    const color = colorVariable
      ? `rgb(${colorVariable})`
      : undefined
    return {
      label: "",
      data: rawDataset?.map(({ value }) => value) || [],
      color: color,
    }
  })
)

const handleHoverDatapoint = (
  hoveredData_: HoveredDataPoint
) => {
  hoveredData.value = hoveredData_
}

const hoveredData = ref<HoveredDataPoint>()

const graphView = ref<GraphView>("trend")

const isExporting = ref(false)
const onExport = async (
  query: SalesReportQuery,
  format?: FileExportFormat
) => {
  if (isExporting.value) return
  isExporting.value = true
  try {
    await exportSalesReport(query, format)
  } finally {
    isExporting.value = false
  }
}

const getFiltersForIndex = (index: number) => {
  const localQuery_ = localQueries[index]
  if (!localQuery_) return
  return [
    ...(localQuery_.cashierId
      ? [getCashierLabel(localQuery_.cashierId).value]
      : []),
    ...(localQuery_.categoryId
      ? [getCategoryLabel(localQuery_.categoryId).value]
      : []),
    ...(localQuery_.productTagId
      ? [
          getProductTagById(localQuery_.productTagId).value
            ?.label,
        ]
      : []),
  ]
}
</script>
